import { useEffect, useRef, useState } from 'react'
import { IHeadCell } from '../../../shared/interfaces/table-display.model';
import { PageOuterContainer } from '../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../presentation-components/display-elements-components/page-heading';
import { LoadingPage } from '../loading-page';
import { TableDisplay } from '../../presentation-components/miscellaneous-components/table-display';
import { TableActionButtons } from '../../presentation-components/miscellaneous-components/table-action-buttons';
import { IconName } from '../../../shared/interfaces/icon-picker.model';
import MeasurementUnitService from '../../../shared/services/measurement-unit/measurement-unit.service';
import { IMeasurementUnit } from '../../../shared/services/transaction/transaction.model';
import { MeasurementUnitDialog } from '../../dialogs/super-admin/add-new-measurement-unit-dialog';
import { IPostMeasurementUnit } from '../../../shared/services/measurement-unit/measurement-unit-model';
import { Grid } from '@mui/material';
import { SearchBar } from '../../presentation-components/display-elements-components/search-bar';
import { useDebounce } from '../../../hooks/use-debounce-hook';

export const SuperAdminMeasurementUnitsPage = () => {
  //Services
  const measurementUnitService = new MeasurementUnitService();
  //State
  const [measurementUnits, setMeasurementUnits] = useState<IMeasurementUnit[]>([]);
  const [selectedMeasurementUnitId, setSelectedMeasurementUnitId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  //Table State
  const [selected, setSelected] = useState<string[]>([]);
  const [inputPage, setInputPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0)
  const [perPage, setPerPage] = useState(10)
  const [change, setChange] = useState<number>(0)
  const [openDialog, setOpenDialog] = useState(false);
  const [orderBy, setOrderBy] = useState<string>('name');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  //Loading State
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasPageMounted, setHasPageMounted] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  //Hooks
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  //Ref
  const requestId = useRef<number>(0)

  useEffect(() => {
    getAllMeasurementUnits()
  }, [inputPage, perPage, change])

  useEffect(() => {
    if (hasPageMounted) {
      if (inputPage === 0) {
        setChange(change + 1)
      } else {
        setInputPage(0)
      }
    }
    setHasPageMounted(true)
  }, [debouncedSearchQuery, orderBy, order])


  const getAllMeasurementUnits = () => {
    requestId.current++
    const currentRequestId = requestId.current
    measurementUnitService.getAllWithPagination(debouncedSearchQuery, inputPage, perPage, orderBy, order)
      .then(response => {
        if (currentRequestId === requestId.current) {
          setTotalRecords(response.count)
          setIsLoading(false)
          setMeasurementUnits(response.rows)
        }
      })
      .catch(err => {
        if (currentRequestId === requestId.current) {
          setIsLoading(false)
        }
        console.log(err)
      })
  }

  const headCells: IHeadCell[] = [
    { id: 'action', numeric: false, label: 'Action', sortable: false, includeInMobile: false },
    { id: 'name', numeric: false, label: 'Name', sortable: true, isHeading: true },
    { id: 'description', numeric: false, label: 'Details', sortable: true },
  ];

  const rows = measurementUnits.map(measurementUnit => {
    return {
      id: measurementUnit.id,
      name: measurementUnit.name,
      description: measurementUnit.description,
      action: (
        <TableActionButtons
          id={measurementUnit.id}
          includeEdit={true}
          onEdit={() => editClickHandler(measurementUnit.id)}
        />
      )
    }
  });

  const editClickHandler = (measurementUnitId: string) => {
    setSelectedMeasurementUnitId(measurementUnitId)
    setOpenDialog(true)
  }

  const submitHandler = (values: IPostMeasurementUnit, resetForm: () => void, isEdit: boolean, measurementUnitId?: string) => {
    if (isEdit && measurementUnitId) {
      setIsSubmitting(true)
      measurementUnitService.edit(measurementUnitId, values)
        .then(response => {
          getAllMeasurementUnits()
          resetForm()
          setIsSubmitting(false)
          setOpenDialog(false)
          setSelectedMeasurementUnitId('')
        })
        .catch(err => {
          setIsSubmitting(false)
          console.log(err)
        })
    }
    else {
      measurementUnitService.create(values)
        .then(response => {
          getAllMeasurementUnits()
          setIsSubmitting(false)
          resetForm()
          setOpenDialog(false)
        })
        .catch(err => {
          setIsSubmitting(false)
          console.log(err)
        })
    }
  }

  // Table Event Handlers
  const handleOrderChange = (orderBy: string, order: 'asc' | 'desc',) => {
    setOrderBy(orderBy)
    setOrder(order)
  };

  return (
    <PageOuterContainer>
      <PageHeading
        title='Super Admin Measurement Units'
        includeBackButton={true}
        backButtonLink='/super-admin'
        backButtonText='Back to super admin'
        includeActionButton={true}
        actionButtonIcon={IconName.Add}
        actionButtonHandler={() => setOpenDialog(true)}
        actionButtonText='Add Measurement Unit'
      />
      <Grid container sx={{ mb: 2 }}>
        <Grid
          item
          xl={4} lg={4} md={6} sm={12} xs={12}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            width: '100%'
          }}>
          <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
        </Grid>
      </Grid>
      <LoadingPage pageReady={!isLoading}>
        {measurementUnits &&
          <TableDisplay
            headCells={headCells}
            rows={rows}
            selectByIcon={true}
            onOrderChange={handleOrderChange}
            selected={selected}
            setSelected={setSelected}
            inputPage={inputPage}
            setInputPage={setInputPage}
            totalRecords={totalRecords}
            perPage={perPage}
            setPerPage={setPerPage}
          />
        }
        {openDialog && selectedMeasurementUnitId &&
          <MeasurementUnitDialog
            open={openDialog}
            isEdit={true}
            isSubmitting={isSubmitting}
            onSubmit={submitHandler}
            measurementUnitId={selectedMeasurementUnitId}
            closeHandler={() => setOpenDialog(false)}
          />
        }
        {
          openDialog && !selectedMeasurementUnitId &&
          <MeasurementUnitDialog
            isSubmitting={isSubmitting}
            onSubmit={submitHandler}
            open={openDialog}
            isEdit={false}
            closeHandler={() => setOpenDialog(false)}
          />
        }
      </LoadingPage>
    </PageOuterContainer>)
}